import { useMutation } from '@zenbusiness/application-commons-graphql';

import { MODAL_NAMES } from '#client/constants/modals';
import { TOKEN_TYPES } from '#client/constants/tokens';
import { useLogError } from '#client/hooks/useLogError';
import { FREEZE_CARD_MUTATION } from '#client/mutations';

import useCardsContext from './useCardsContext';
import useConstBusinessEntityUuid from './useConstBusinessEntityUuid';
import useCustomerToken from './useCustomerToken';
import useModalContext from './useModalContext';
import useToastContext from './useToastContext';
import useUnitAccountId from './useUnitAccountId';

export default function useFreezeCard(id, handleClose) {
  const { businessEntityUuid } = useConstBusinessEntityUuid();
  const { unitAccountId } = useUnitAccountId();
  const { refetchCard, loading: cardsLoading } = useCardsContext();
  const { createSuccessToast, createCommonErrorToast } = useToastContext();
  const { openModal, clearAllModal } = useModalContext();

  const handleCloseModal = handleClose ?? clearAllModal;

  const { getCustomerToken, loading: tokenLoading } = useCustomerToken({
    type: TOKEN_TYPES.COMMON
  });

  const [freezeCard, { loading: freezeCardLoading, error: freezeCardError, errors }] = useMutation(
    FREEZE_CARD_MUTATION,
    {
      noThrow: true
    }
  );

  useLogError(errors, freezeCardError);
  const handleFreezeCard = async () => {
    const customerToken = await getCustomerToken();

    if (!customerToken) {
      clearAllModal();
      return;
    }

    const { error, data } = await freezeCard({
      variables: {
        input: {
          identifier: {
            businessUuid: businessEntityUuid,
            unitAccountId
          },
          id,
          customerToken
        }
      }
    });
    if (!error && data) {
      await refetchCard();
      handleCloseModal();
      createSuccessToast({
        title: `Your card ending in ${data?.freezeCard?.last4Digits} is frozen`,
        secondaryAction: {
          label: 'Undo',
          size: 'sm',
          onClick: () => {
            openModal({
              name: MODAL_NAMES.UNFREEZE_CARD_MODAL,
              cardId: data?.freezeCard?.id,
              last4Digits: data?.freezeCard?.last4Digits
            });
          }
        }
      });
    } else {
      createCommonErrorToast({ error });
    }
  };

  return {
    handleFreezeCard,
    loading: freezeCardLoading || cardsLoading || tokenLoading
  };
}
